import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useMemo, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { PageHeader } from "@/components/shared/PageHeader";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { StatCard } from "@/components/shared/StatCard";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import * as Icons from "lucide-react";
import { cn } from "@/lib/utils";
import { useCollection } from "@/hooks/useCollection";
import { adapter } from "@/services/db";
import { auth } from "@/lib/firebase";
import { Timestamp } from "firebase/firestore";
import { toast } from "sonner";
import { text, list, inr, fmtTs, toDate } from "@/lib/format";
import { AllFieldsPanel } from "@/components/explorer/AllFieldsPanel";
import { AllFieldsTable } from "@/components/shared/AllFieldsTable";
import { RequestRawData } from "@/components/requests/RequestRawData";
import { GroupLeadsSwitch } from "@/components/requests/GroupLeadsSwitch";
import { FormResponses } from "@/components/requests/FormResponses";
import { jobSizeLabel, jobSizeOf, startWhenLabel, visitLabel, JOB_SIZES } from "@/lib/requestLabels";
import { DateRangeFilter, useRange, initialRange } from "@/components/shared/DateRangeFilter";
import { inRange } from "@/lib/dateRange";
import { EditRecordButton } from "@/components/explorer/RecordEditor";
import { BulkBar, SelectAllBox, SelectBox, useSelection } from "@/components/shared/BulkSelect";
import { NewDot, HealthChip, NewActivityBar, useRowSignals } from "@/components/shared/RowSignals";
import { healthOf, healthRowClass } from "@/lib/health";

const REQ_DATE_FIELDS = [{ key: "created_at", label: "Created" }, { key: "general_phase_started_at", label: "Floated" }, { key: "hired_at", label: "Hired" }, { key: "updated_at", label: "Last updated" }];

// Standardised Lead ID: last 6 chars of the request doc id, uppercased. Stored on
// requests/{id}.lead_code; fall back to computing it if the field isn't set yet.
const leadCode = (r) => (r.lead_code || String(r.id).slice(-6).toUpperCase());

// Category status vocabulary (matches the spec).
const CAT_STATUS = {
  not_floated: { label: "Not Floated", cls: "bg-slate-100 text-slate-600 border-slate-200" },
  awaiting: { label: "Awaiting Professional", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  responses: { label: "Responses Received", cls: "bg-cyan-50 text-cyan-700 border-cyan-200" },
  selected: { label: "Professional Selected", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  no_response: { label: "No Response", cls: "bg-red-50 text-red-700 border-red-200" },
  cancelled: { label: "Customer Cancelled", cls: "bg-red-50 text-red-700 border-red-200" },
};

// Float escalation is flag-driven (no timer in Command Centre). The 10-/14-min
// flags are written by Brugo backend onto the lead_distribution doc; we only read
// them here. A lead is "active" (alarmable / extendable) until it responds or its
// status becomes responded/expired/cancelled.
const LEAD_ACTIVE = (l) => !l.responded_at && !["responded", "expired", "cancelled"].includes(l.status);
const leadAlarm = (l) => {
  if (!LEAD_ACTIVE(l)) return null;
  if (l.expiry_warning_14min) return "warn14";
  if (l.no_response_10min) return "warn10";
  return null;
};

export default function Requests() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "all";
  const requests = useCollection("requests");
  const users = useCollection("users");
  const pros = useCollection("professionals");
  const responses = useCollection("professional_responses");
  const leadDist = useCollection("lead_distributions");
  const reqReqs = useCollection("request_requirements");
  const projects = useCollection("projects");
  const categories = useCollection("categories");
  // brugo.in requests paid outside operating hours (held until opening) + the lead email log.
  const scheduledAll = useCollection("scheduled_requests");
  const emailLog = useCollection("lead_email_log");
  const waitingReqs = useMemo(() => scheduledAll.filter((x) => x.status === "scheduled")
    .sort((a, b) => (toDate(a.float_at)?.getTime() || 0) - (toDate(b.float_at)?.getTime() || 0)), [scheduledAll]);
  const emailById = useMemo(() => Object.fromEntries(emailLog.map((e) => [e.id, e])), [emailLog]);
  const [openId, setOpenId] = useState(null);
  const [q, setQ] = useState("");
  const [dr, setDr] = useState(initialRange());
  const [view, setView] = useState("cards");
  const [jobF, setJobF] = useState("all");
  const range = useRange(dr);
  const userById = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);
  const proById = useMemo(() => Object.fromEntries(pros.map((p) => [p.id, p])), [pros]);
  const catNameById = useMemo(() => Object.fromEntries(categories.map((c) => [c.id, c.name])), [categories]);
  const respByReq = useMemo(() => { const m = {}; responses.forEach((r) => { (m[r.request_id] ||= []).push(r); }); return m; }, [responses]);
  const leadByReq = useMemo(() => { const m = {}; leadDist.forEach((l) => { (m[l.request_id] ||= []).push(l); }); return m; }, [leadDist]);
  const reqReqByReq = useMemo(() => { const m = {}; reqReqs.forEach((r) => { (m[r.request_id] ||= []).push(r); }); return m; }, [reqReqs]);
  const projByReq = useMemo(() => { const m = {}; projects.forEach((p) => { if (p.request_id) m[p.request_id] = p; }); return m; }, [projects]);

  // Service categories for a request (id + display name), parallel arrays in the data.
  const catsOf = (r) => {
    const ids = Array.isArray(r.service_category_ids) ? r.service_category_ids : [];
    const names = Array.isArray(r.services) ? r.services : [];
    if (ids.length) return ids.map((id, i) => ({ id, name: catNameById[id] || names[i] || id }));
    if (names.length) return names.map((n) => ({ id: null, name: n }));
    return [{ id: r.category, name: r.subcategory || catNameById[r.category] || r.category || "Service" }];
  };
  const catKey = (c) => c.id || String(c.name).toLowerCase();
  // category_ids also carries the services linked through professional categories (Catalog → Professional categories).
  const proMatchesCat = (pro, c) => !!pro && ((c.id && pro.category_id === c.id) || (c.id && Array.isArray(pro.category_ids) && pro.category_ids.map(String).includes(String(c.id))) || (pro.category_name && String(pro.category_name).toLowerCase() === String(c.name).toLowerCase()));

  // Selected categories = categories of the professionals on the linked project.
  const selectedCatKeys = (r) => {
    const proj = projByReq[r.id];
    const s = new Set();
    if (proj) {
      const ids = Array.isArray(proj.professional_ids) && proj.professional_ids.length ? proj.professional_ids : [proj.professional_id].filter(Boolean);
      ids.forEach((id) => { const p = proById[id]; if (p) s.add(p.category_id || String(p.category_name || "").toLowerCase()); });
    }
    return s;
  };

  // Per-category live metrics + derived status.
  const catStats = (r, c) => {
    const leads = (leadByReq[r.id] || []).filter((l) => proMatchesCat(proById[l.professional_id], c));
    const resp = (respByReq[r.id] || []).filter((x) => proMatchesCat(proById[x.professional_id], c));
    const contacted = leads.length;
    const responded = new Set([...resp.map((x) => x.professional_id), ...leads.filter((l) => l.status === "responded" || l.responded_at).map((l) => l.professional_id)]).size;
    const rejected = leads.filter((l) => ["rejected", "declined"].includes(l.status)).length;
    const expired = leads.filter((l) => l.status === "expired").length;
    const noResponse = expired;
    const selected = selectedCatKeys(r).has(catKey(c));
    let status = "awaiting";
    if (r.customer_status === "cancelled") status = "cancelled";
    else if (selected) status = "selected";
    else if (contacted === 0) status = "not_floated";
    else if (responded > 0) status = "responses";
    else if (expired >= contacted && contacted > 0) status = "no_response";
    else status = "awaiting";
    return { contacted, responded, rejected, expired, noResponse, accepted: selected ? 1 : 0, status };
  };

  const rows = useMemo(() => requests
    .filter((r) => tab === "all" ? true : tab === "ops" ? r.internal_status === "ops_required" : tab === "hired" ? r.customer_status === "hired" : tab === "no_response" ? r.customer_status === "no_responses" : true)
    .filter((r) => inRange(r[dr.field], range))
    .filter((r) => jobF === "all" || (jobF === "none" ? !jobSizeOf(r) : jobSizeOf(r) === jobF))
    .filter((r) => {
      if (!q) return true;
      const needle = q.trim().toLowerCase().replace(/^lead\s+/, "");
      return leadCode(r).toLowerCase().includes(needle) || JSON.stringify(r).toLowerCase().includes(needle);
    })
    .sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || ""))), [requests, tab, q, dr.field, range, jobF]);

  const counts = useMemo(() => ({
    ops: requests.filter((r) => r.internal_status === "ops_required").length,
    hired: requests.filter((r) => r.customer_status === "hired").length,
    noResp: requests.filter((r) => r.customer_status === "no_responses").length,
  }), [requests]);

  const sel = requests.find((r) => r.id === openId) || null;
  const sig = useRowSignals("requests", "requests");
  const pick = useSelection(rows);
  const openLead = (id) => { sig.opened(id); setOpenId(id); };
  const hotLeads = requests.filter((r) => healthOf("requests", r)).length;

  return (
    <div>
      <PageHeader title="Requests & Leads" subtitle="Each customer request is ONE lead. Multi-service requests track every service category separately with its own live status — click a lead for the full journey, bottleneck and per-category response data." actions={<><NewActivityBar section="requests" colls={["requests"]} /><GroupLeadsSwitch /></>} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard testId="req-kpi-total" index={0} label="Leads" value={String(requests.length)} icon="Send" accent="primary" />
        <StatCard testId="req-kpi-ops" index={1} label="Ops Required" value={String(counts.ops)} icon="TriangleAlert" accent="amber" critical={counts.ops > 0} />
        <StatCard testId="req-kpi-hired" index={2} label="Hired" value={String(counts.hired)} icon="CircleCheckBig" accent="emerald" />
        <StatCard testId="req-kpi-noresp" index={3} label="No Responses" value={String(counts.noResp)} icon="Clock" accent="red" />
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px] max-w-sm"><Icons.Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" /><Input data-testid="req-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search leads…" className="pl-9" /></div>
        <DateRangeFilter value={dr} onChange={setDr} fields={REQ_DATE_FIELDS} testId="req-date-filter" />
        <Select value={jobF} onValueChange={setJobF}>
          <SelectTrigger className="h-9 w-36 text-xs" data-testid="req-jobsize-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all" className="text-xs">All job sizes</SelectItem>
            {Object.entries(JOB_SIZES).map(([k, l]) => <SelectItem key={k} value={k} className="text-xs">{l}</SelectItem>)}
            <SelectItem value="none" className="text-xs">Not captured (old)</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-xs text-muted-foreground font-mono" data-testid="req-shown-count">{rows.length} shown</span>
        {hotLeads > 0 && <span className="text-xs text-red-600 font-medium flex items-center gap-1"><Icons.Siren className="h-3.5 w-3.5" />{hotLeads} critical / stuck</span>}
        {view === "cards" && <label className="ml-auto flex items-center gap-2 text-xs text-muted-foreground"><SelectAllBox s={pick} testId="req-select-all" />Select shown</label>}
      </div>
      {waitingReqs.length > 0 && (
        <Card className="p-4 mb-4 border-indigo-200 bg-indigo-50/40" data-testid="req-scheduled-card">
          <p className="font-heading font-semibold text-foreground flex items-center gap-2"><Icons.MoonStar className="h-4 w-4 text-indigo-600" /> Waiting for opening time ({waitingReqs.length})</p>
          <p className="text-xs text-muted-foreground mt-0.5 mb-3">Paid outside operating hours (System Control → Website leads). Each one goes to professionals automatically at its time.</p>
          <div className="space-y-1.5">
            {waitingReqs.map((w) => {
              const em = emailById[w.id];
              const c = userById[w.customer_id];
              return (
                <div key={w.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-indigo-100 bg-white px-3 py-2" data-testid={`req-scheduled-${w.id}`}>
                  <div className="min-w-0">
                    <span className="font-heading font-bold text-sm">Lead {w.lead_code || String(w.id).slice(-6).toUpperCase()}</span>
                    <span className="text-xs text-muted-foreground ml-2">{text(w.private_contact?.contact_name || c?.name, null) || "Customer"} · {text(w.title || (w.services || []).join(", "))} · {text(w.city)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <EmailChip e={em} />
                    <span className="rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[11px] font-semibold text-indigo-700">Floats {fmtTs(w.float_at)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}
      <Tabs value={tab} onValueChange={(v) => setParams({ tab: v })} className="mb-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
        <TabsList>
          <TabsTrigger value="all" data-testid="req-tab-all">All ({requests.length})</TabsTrigger>
          <TabsTrigger value="ops" data-testid="req-tab-ops">Ops Required</TabsTrigger>
          <TabsTrigger value="hired" data-testid="req-tab-hired">Hired</TabsTrigger>
          <TabsTrigger value="no_response" data-testid="req-tab-noresp">No Responses</TabsTrigger>
        </TabsList>
        <div className="inline-flex rounded-lg border border-border p-0.5 bg-secondary/50" data-testid="req-view-toggle">
          {[["cards", "Lead cards"], ["requests", "requests · all fields"], ["ld", "lead_distributions · all fields"]].map(([k, l]) => (
            <button key={k} onClick={() => setView(k)} className={cn("px-3 py-1 text-xs rounded-md transition-colors", view === k ? "bg-background shadow-sm text-foreground font-medium" : "text-muted-foreground hover:text-foreground")} data-testid={`req-view-${k}`}>{l}</button>
          ))}
        </div>
        </div>
      </Tabs>

      {view === "requests" && <AllFieldsTable rows={rows} onOpen={(r) => setOpenId(r.id)} testId="req-all-table" path="requests" />}
      {view === "ld" && <AllFieldsTable rows={rows.flatMap((r) => leadByReq[r.id] || [])} onOpen={(l) => setOpenId(l.request_id)} testId="ld-all-table" path="lead_distributions" />}
      {view === "cards" && <BulkBar s={pick} path="requests" rows={requests} label="leads" nameOf={(r) => `Lead ${leadCode(r)}`} actions={[{ key: "extend", label: "Extend +5 min", icon: "AlarmClockPlus", eligible: (r) => (leadByReq[r.id] || []).some(LEAD_ACTIVE), skipNote: "no professional is still deciding on them", description: "Gives every professional still deciding on each lead 5 more minutes — same as the button inside a lead.", run: (r) => extendLeadBy5(r, leadByReq[r.id] || [], auth?.currentUser?.email || auth?.currentUser?.uid || "ops") }]} fields={[{ key: "internal_status", label: "Internal status" }, { key: "customer_status", label: "Customer status" }, { key: "city", label: "City" }]} />}
      <div className={cn("space-y-3", view !== "cards" && "hidden")}>
        {rows.map((r) => {
          const cats = catsOf(r);
          const cust = userById[r.customer_id];
          const rowLeads = leadByReq[r.id] || [];
          const anyWarn14 = rowLeads.some((l) => leadAlarm(l) === "warn14");
          const anyWarn10 = !anyWarn14 && rowLeads.some((l) => leadAlarm(l) === "warn10");
          return (
            <Card key={r.id} className={cn("p-4 hover:shadow-md transition-shadow cursor-pointer", healthRowClass(healthOf("requests", r)), pick.sel.has(r.id) && "ring-1 ring-primary/40 bg-primary/[0.03]")} onClick={() => openLead(r.id)} data-testid={`lead-row-${r.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex gap-3">
                  <div className="pt-1"><SelectBox s={pick} id={r.id} /></div>
                  <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <NewDot show={sig.isNew(r.id)} />
                    <span className="font-heading font-bold text-foreground">Lead {leadCode(r)}</span>
                    <span className="text-[11px] font-mono text-muted-foreground" data-testid={`lead-fullid-${r.id}`}>{r.id}</span>
                    <StatusBadge status={r.internal_status || r.customer_status || "open"} />
                    {jobSizeOf(r) && <span className="rounded-full border border-border bg-secondary px-2 py-0.5 text-[11px]" data-testid={`lead-jobsize-${r.id}`}>Size: {jobSizeLabel(r)}</span>}
                    {startWhenLabel(r) !== "—" && <span className="rounded-full border border-border bg-secondary px-2 py-0.5 text-[11px]" data-testid={`lead-start-${r.id}`}>{startWhenLabel(r)}</span>}
                    {(r.ai_intake?.entry_path === "diwali_lighting" || r.customer_form_responses?.form === "diwali_lighting") && <span className="rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-800" data-testid={`lead-diwali-${r.id}`}>Diwali Lighting</span>}
                    {r.float_scheduled && <span className="rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[11px] text-indigo-700" data-testid={`lead-overnight-${r.id}`}>Paid overnight · floated {fmtTs(r.float_scheduled.float_at)}</span>}
                    <EmailChip e={emailById[r.id]} />
                    {anyWarn14 && <span className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-700" data-testid={`lead-alarm-${r.id}`}><Icons.AlarmClock className="h-3 w-3" /> Expiry warning</span>}
                    {anyWarn10 && <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700" data-testid={`lead-alarm-${r.id}`}><Icons.Bell className="h-3 w-3" /> No response 10m</span>}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">{text(cust?.name || cust?.full_name, null) || `Customer ${String(r.customer_id).slice(0, 6)}`} · {text(r.city)} · {fmtTs(r.created_at)}</p>
                  {healthOf("requests", r) && <div className="mt-1.5"><HealthChip h={healthOf("requests", r)} /></div>}
                  </div>
                </div>
                <div className="flex items-center shrink-0" onClick={(e) => e.stopPropagation()}>
                  <EditRecordButton path="requests" id={r.id} />
                  <Button size="sm" variant="ghost" className="shrink-0" onClick={() => openLead(r.id)} data-testid={`lead-open-${r.id}`}>Open <Icons.ChevronRight className="h-4 w-4 ml-1" /></Button>
                </div>
              </div>
              <div className="mt-3 grid gap-1.5">
                {cats.map((c, i) => {
                  const st = catStats(r, c);
                  const meta = CAT_STATUS[st.status];
                  return (
                    <div key={catKey(c) + i} className={cn("flex items-center justify-between rounded-lg border border-border/70 bg-secondary/30 px-3 py-1.5", st.status === "no_response" && "cc-stuck")} data-testid={`lead-${r.id}-cat-${i}`}>
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-xs font-mono text-muted-foreground w-4 shrink-0">{i + 1}</span>
                        <span className="text-sm font-medium text-foreground truncate">{c.name}</span>
                        <span className="text-[11px] text-muted-foreground hidden sm:inline">· {st.contacted} contacted · {st.responded} responded</span>
                      </div>
                      <span className={cn("inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium shrink-0", meta.cls)}>{meta.label}</span>
                    </div>
                  );
                })}
              </div>
            </Card>
          );
        })}
        {rows.length === 0 && <Card className="p-10 text-center text-muted-foreground">No leads in this view.</Card>}
      </div>

      {sel && <LeadDetail r={sel} onClose={() => setOpenId(null)}
        cats={catsOf(sel)} catStats={(c) => catStats(sel, c)} catKey={catKey}
        cust={userById[sel.customer_id]} proById={proById}
        leads={leadByReq[sel.id] || []} resp={respByReq[sel.id] || []}
        reqReq={(reqReqByReq[sel.id] || [])[0]} project={projByReq[sel.id]} leadCode={leadCode(sel)} />}
    </div>
  );
}

// Lead email status (lead_email_log/{requestId}, written by the emailNewRequest function).
function EmailChip({ e }) {
  if (!e) return null;
  if (e.status === "sent") return <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] text-emerald-700" title={(e.to || []).join(", ")} data-testid={`lead-email-${e.id}`}><Icons.MailCheck className="h-3 w-3" /> Email sent {fmtTs(e.sent_at)}</span>;
  if (e.status === "failed") return <span className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-700" title={e.error || ""} data-testid={`lead-email-${e.id}`}><Icons.MailX className="h-3 w-3" /> Email failed</span>;
  if (e.status === "off") return <span className="rounded-full border border-border bg-secondary px-2 py-0.5 text-[11px] text-muted-foreground" data-testid={`lead-email-${e.id}`}>Emails off</span>;
  return null;
}

// Manual "Extend +5 min" at LEAD level (used by the lead drawer and the bulk bar).
// Source of truth = general_phase_ends_at; the request and every active lead_distribution get the
// SAME new value in ONE atomic batch. Returns how many professionals were extended.
async function extendLeadBy5(r, leads, by) {
    const active = leads.filter(LEAD_ACTIVE);
    if (!active.length) throw new Error("No active professionals on this lead to extend.");
    const now = new Date();
    const leadTimes = active.map((l) => toDate(l.general_phase_ends_at) || toDate(l.current_expires_at) || toDate(l.expires_at)).filter(Boolean);
    const base = toDate(r.general_phase_ends_at)
      || (leadTimes.length ? new Date(Math.max(...leadTimes.map((d) => d.getTime()))) : null)
      || now;
    const ts = Timestamp.fromDate(new Date(base.getTime() + 5 * 60000));
    const nowTs = Timestamp.fromDate(now);
    const items = [
      { coll: "requests", id: r.id, patch: {
        general_phase_ends_at: ts, current_expires_at: ts,
        extension_count: (r.extension_count || 0) + 1,
        extension_total_minutes: (r.extension_total_minutes || 0) + 5,
        last_extended_at: nowTs, last_extended_by: by, updated_at: nowTs,
      } },
      ...active.map((l) => ({ coll: "lead_distributions", id: l.id, patch: {
        general_phase_ends_at: ts, current_expires_at: ts, expires_at: ts,
        extension_count: (l.extension_count || 0) + 1,
        extension_total_minutes: (l.extension_total_minutes || 0) + 5,
        last_extended_at: nowTs, last_extended_by: by,
        no_response_10min: false, no_response_10min_at: null,
        expiry_warning_14min: false, expiry_warning_14min_at: null,
        updated_at: nowTs,
      } })),
    ];
  await adapter.batchUpdatePaths(items);
  return active.length;
}

function LeadDetail({ r, onClose, cats, catStats, catKey, cust, proById, leads, resp, reqReq, project, leadCode }) {
  // Address + contact now live in gated subdocs: requests/{id}/private/address
  // (full address, no PII) and requests/{id}/private/contact (name + phone,
  // staff/owner only). Fetched on open; fall back to public fields if absent
  // (e.g. before the backfill migration / before Brugo Web switches over).
  const [addr, setAddr] = useState(null);
  const [contact, setContact] = useState(null);
  useEffect(() => {
    let alive = true;
    adapter.get(`requests/${r.id}/private`, "address").then((d) => { if (alive) setAddr(d); }).catch(() => {});
    adapter.get(`requests/${r.id}/private`, "contact").then((d) => { if (alive) setContact(d); }).catch(() => {});
    return () => { alive = false; };
  }, [r.id]);

  // Manual "Extend +5 min" at LEAD level. Source of truth = general_phase_ends_at.
  // Base off the request's current general_phase_ends_at (fallback: latest lead
  // expiry), add +5 min, then write the SAME new value to the request doc AND every
  // active lead_distribution in ONE atomic cross-collection batch — so requests and
  // lead_distributions stay synchronized and Pro app / Brugo Web / CC update in real
  // time. Also mirrors expires_at/current_expires_at on leads for legacy timers, and
  // clears the 10/14-min flags. Backend must treat general_phase_ends_at as the timer.
  const onExtendLead = async () => {
    try {
      const n = await extendLeadBy5(r, leads, auth?.currentUser?.email || auth?.currentUser?.uid || "ops");
      toast.success(`Lead extended +5 min for ${n} professional${n > 1 ? "s" : ""} (request + leads synced).`);
    } catch (e) {
      toast.error(e.message?.startsWith("No active") ? e.message : `Extend failed: ${e.code || e.message}`);
    }
  };
  const escalationLeads = leads.filter((l) => LEAD_ACTIVE(l) || l.no_response_10min || l.expiry_warning_14min || l.extension_count);

  const firstLeadAt = leads.reduce((m, l) => { const t = l.created_at; return (!m || (t && String(t) < String(m))) ? t : m; }, null);
  const responded = resp.length || leads.filter((l) => l.status === "responded" || l.responded_at).length;
  const floated = leads.length || (r.lead_distribution_stats?.total ?? 0);
  const hasPreferred = leads.some((l) => l.is_preferred || l.phase === "preferred") || (Array.isArray(r.preferred_professional_ids) && r.preferred_professional_ids.length);
  const hasGeneral = leads.some((l) => l.phase === "general") || !!r.general_phase_started_at;

  const steps = [
    { label: "Request created", at: r.created_at, done: true },
    { label: "Categories selected", at: r.created_at, done: cats.length > 0 },
    { label: "Lead floated", at: r.general_phase_started_at || firstLeadAt, done: floated > 0 },
    { label: "Preferred professionals notified", at: r.pref_phase_ends_at || firstLeadAt, done: !!hasPreferred },
    { label: "General pool opened", at: r.general_phase_opens_at || r.general_phase_started_at, done: !!hasGeneral },
    { label: "Responses received", at: r.ready_for_customer_at, done: responded > 0 },
    { label: "Customer viewed responses", at: r.ready_for_customer_at, done: !!r.ready_for_customer_at || (r.customer_status && !["no_responses", "sourcing"].includes(r.customer_status)) },
    { label: "Professional selected", at: project?.created_at, done: !!project },
    { label: "Project created", at: project?.created_at, done: !!project?.project_number },
  ];

  // Current bottleneck (one line).
  let bottleneck = "CURRENTLY AT: Awaiting Professional Response";
  if (r.customer_status === "cancelled") bottleneck = "CLOSED: Customer cancelled the request";
  else if (project) bottleneck = `COMPLETED: Professional hired · Project #${text(project.project_number)} created`;
  else if (r.customer_status === "hired") bottleneck = "COMPLETED: Professional hired";
  else if (floated === 0) bottleneck = "CURRENTLY STUCK AT: Lead not floated (no professionals notified yet)";
  else if (responded === 0 && leads.length > 0 && leads.every((l) => l.status === "expired")) bottleneck = "CURRENTLY STUCK AT: No professional responded (all leads expired)";
  else if (responded === 0) bottleneck = "CURRENTLY STUCK AT: Awaiting Professional Response";
  else if (responded > 0 && !r.ready_for_customer_at) bottleneck = "CURRENTLY STUCK AT: Awaiting Ops to release responses to customer";
  else if (responded > 0) bottleneck = "CURRENTLY STUCK AT: Awaiting customer to select a professional";

  const details = reqReq || {};
  const summary = r.professional_summary && typeof r.professional_summary === "object" ? r.professional_summary : {};
  const serviceDetails = summary.service_details && typeof summary.service_details === "object" ? summary.service_details : {};
  const prefPros = (r.preferred_professional_ids || []).map((id) => text(proById[id]?.full_name, id));

  const a = addr || {};
  const addrFormatted = text(a.formatted_address, null)
    || [a.house_flat_plot, a.building_society, a.landmark, a.area || a.line].filter(Boolean).join(", ")
    || null;
  const mapsUrl = (a.lat != null && a.lng != null)
    ? `https://www.google.com/maps/search/?api=1&query=${a.lat},${a.lng}`
    : (a.formatted_address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(a.formatted_address)}` : null);

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto" data-testid="lead-detail">
        <SheetHeader className="text-left">
          <SheetTitle className="font-heading">Lead {leadCode}</SheetTitle>
          <SheetDescription className="font-mono">{r.id}</SheetDescription>
        </SheetHeader>

        <div className={cn("mt-3 rounded-lg border px-3 py-2 text-sm font-medium",
          bottleneck.startsWith("COMPLETED") ? "bg-emerald-50 text-emerald-700 border-emerald-200"
            : bottleneck.startsWith("CLOSED") ? "bg-slate-100 text-slate-600 border-slate-200"
              : "bg-amber-50 text-amber-800 border-amber-200")} data-testid="lead-bottleneck">
          {bottleneck}
        </div>

        {escalationLeads.length > 0 && (
          <div className="mt-4" data-testid="lead-escalation">
            <div className="flex items-center justify-between mb-2 gap-2">
              <div>
                <h4 className="font-heading font-semibold text-foreground flex items-center gap-2 text-sm"><Icons.AlarmClock className="h-4 w-4 text-primary" /> Float escalation</h4>
                <p className="text-[11px] font-mono text-muted-foreground mt-0.5">Lead closes: {fmtTs(r.general_phase_ends_at)}{r.extension_count ? ` · +${r.extension_total_minutes || r.extension_count * 5}m (${r.extension_count}×)` : ""}</p>
              </div>
              {leads.some(LEAD_ACTIVE) && <Button size="sm" onClick={onExtendLead} className="h-7 px-2.5 text-xs" data-testid="extend-lead"><Icons.Plus className="h-3 w-3 mr-1" /> Extend +5 min (all pros)</Button>}
            </div>
            <div className="space-y-2">
              {escalationLeads.map((l) => {
                const alarm = leadAlarm(l);
                const pro = proById[l.professional_id];
                const exp = l.general_phase_ends_at || l.current_expires_at || l.expires_at;
                return (
                  <div key={l.id} className="rounded-lg border border-border p-2.5" data-testid={`escalation-${l.id}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{text(pro?.full_name) === "—" ? text(l.professional_id) : text(pro?.full_name)}</p>
                        <p className="text-[11px] font-mono text-muted-foreground">{text(l.phase)} · expires {fmtTs(exp)}{l.extension_count ? ` · +${l.extension_total_minutes || l.extension_count * 5}m (${l.extension_count}×)` : ""}</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {alarm === "warn14" && <span className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-700"><Icons.AlarmClock className="h-3 w-3" /> 14-min warning</span>}
                        {alarm === "warn10" && <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700"><Icons.Bell className="h-3 w-3" /> 10-min no response</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <Section icon="User" title="Customer">
          <KV k="Name" v={text(contact?.contact_name || cust?.name || cust?.full_name, null) || `Customer ${String(r.customer_id).slice(0, 6)}`} />
          <KV k="Phone" v={text(contact?.contact_phone || cust?.phone)} />
          <KV k="Area" v={`${text(r.city)}${summary.location?.city && summary.location.city !== r.city ? ` · ${summary.location.city}` : ""}${Array.isArray(summary.areas) && summary.areas.length ? ` · ${summary.areas.join(", ").replace(/_/g, " ")}` : ""}`} />
        </Section>

        <Section icon="MapPin" title="Service address">
          {addr ? (<>
            {addrFormatted && <KV k="Address" v={addrFormatted} />}
            {addr.house_flat_plot && <KV k="House / Flat / Plot" v={text(addr.house_flat_plot)} />}
            {addr.building_society && <KV k="Building / Society" v={text(addr.building_society)} />}
            {addr.landmark && <KV k="Landmark" v={text(addr.landmark)} />}
            <KV k="City / Pincode" v={`${text(addr.city || r.city)}${(addr.pincode || r.pincode) ? ` · ${text(addr.pincode || r.pincode)}` : ""}`} />
            {mapsUrl && <a href={mapsUrl} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline" data-testid="lead-open-maps"><Icons.MapPin className="h-3.5 w-3.5" /> Open in Maps</a>}
          </>) : (
            <KV k="City / Pincode" v={`${text(r.city)}${r.pincode ? ` · ${text(r.pincode)}` : ""}`} />
          )}
        </Section>

        <Section icon="ClipboardList" title="Requirement">
          <KV k="Requested" v={list(r.services, ", ", text(r.category))} />
          <KV k="Service categories" v={cats.map((c) => c.name).join(", ")} />
          {summary.property_type && <KV k="Property" v={`${text(summary.property_type)}${summary.property_occupied != null ? ` · ${summary.property_occupied ? "occupied" : "vacant"}` : ""}`} />}
          <KV k="Job size" v={<span data-testid="req-job-size">{jobSizeLabel(r)}</span>} />
          <KV k="Preferred visit" v={visitLabel(r)} />
          <KV k="How soon" v={<span data-testid="req-start-when">{startWhenLabel(r)}</span>} />
          {summary.urgency && <KV k="Urgency" v={text(summary.urgency)} />}
          {details.scope && <KV k="Scope" v={text(details.scope)} />}
          {(details.notes || summary.notes) && <KV k="Notes" v={text(details.notes || summary.notes)} />}
          <KV k="Preferred professionals" v={prefPros.length ? prefPros.join(", ") : "None specified"} />
          <FormResponses cfr={r.customer_form_responses} />
          {Object.keys(serviceDetails).length > 0 && (
            <div className="pt-1">
              <p className="text-[11px] font-semibold text-foreground mb-1">Specific requirements per service</p>
              {Object.entries(serviceDetails).map(([svc, items]) => (
                <div key={svc} className="flex justify-between gap-3 text-sm"><span className="text-muted-foreground capitalize">{svc}</span><span className="text-foreground text-right">{Array.isArray(items) ? items.join(", ") : text(items)}</span></div>
              ))}
            </div>
          )}
        </Section>

        {/* Per-category live tracking */}
        <div className="mt-5">
          <h4 className="font-heading font-semibold text-foreground mb-2 flex items-center gap-2 text-sm"><Icons.Layers className="h-4 w-4 text-primary" /> Per-category tracking</h4>
          <div className="rounded-lg border border-border overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border text-left text-[11px] uppercase text-muted-foreground">
                <th className="p-2">Category</th><th className="p-2">Status</th><th className="p-2 text-center">Contacted</th><th className="p-2 text-center">Resp.</th><th className="p-2 text-center">Accepted</th><th className="p-2 text-center">Rejected</th><th className="p-2 text-center">No resp.</th><th className="p-2 text-center">Expired</th>
              </tr></thead>
              <tbody>
                {cats.map((c, i) => {
                  const st = catStats(c); const meta = CAT_STATUS[st.status];
                  return (
                    <tr key={catKey(c) + i} className="border-b border-border/60 last:border-0" data-testid={`lead-cat-row-${i}`}>
                      <td className="p-2 font-medium text-foreground">{i + 1}. {c.name}</td>
                      <td className="p-2"><span className={cn("inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium", meta.cls)}>{meta.label}</span></td>
                      <td className="p-2 text-center font-mono">{st.contacted}</td>
                      <td className="p-2 text-center font-mono">{st.responded}</td>
                      <td className="p-2 text-center font-mono">{st.accepted}</td>
                      <td className="p-2 text-center font-mono">{st.rejected}</td>
                      <td className="p-2 text-center font-mono">{st.noResponse}</td>
                      <td className="p-2 text-center font-mono">{st.expired}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Journey timeline */}
        <div className="mt-5">
          <h4 className="font-heading font-semibold text-foreground mb-3 flex items-center gap-2 text-sm"><Icons.GitCommitHorizontal className="h-4 w-4 text-primary" /> Lead Journey</h4>
          <div className="relative pl-5">
            <div className="absolute left-[7px] top-1 bottom-1 w-px bg-border" />
            <div className="space-y-3">
              {steps.map((s, i) => (
                <div key={i} className="relative flex items-start gap-3" data-testid={`journey-step-${i}`}>
                  <div className={cn("relative z-10 -ml-5 mt-0.5 flex h-4 w-4 items-center justify-center rounded-full border", s.done ? "bg-primary border-primary" : "bg-card border-border")}>
                    {s.done && <Icons.Check className="h-2.5 w-2.5 text-primary-foreground" />}
                  </div>
                  <div className="flex-1 -mt-0.5">
                    <p className={cn("text-sm", s.done ? "text-foreground font-medium" : "text-muted-foreground")}>{s.label}</p>
                    {s.done && s.at && <p className="text-[11px] font-mono text-muted-foreground">{fmtTs(s.at)}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Responses */}
        {resp.length > 0 && (
          <div className="mt-5">
            <h4 className="font-heading font-semibold text-foreground mb-2 flex items-center gap-2 text-sm"><Icons.MessageSquareReply className="h-4 w-4 text-primary" /> Responses ({resp.length})</h4>
            <div className="space-y-2">
              {resp.map((x) => { const pro = proById[x.professional_id]; return (
                <div key={x.id} className="rounded-lg border border-border p-2.5 flex items-center justify-between" data-testid={`lead-resp-${x.id}`}>
                  <div><p className="text-sm font-medium text-foreground">{text(pro?.full_name) === "—" ? text(x.professional_id) : text(pro?.full_name)}</p><p className="text-[11px] font-mono text-muted-foreground">{text(pro?.category_name)} · {text(pro?.phone)}</p></div>
                  <div className="text-right"><p className="font-mono text-sm text-foreground">{x.indicative_price ? inr(x.indicative_price) : "—"}</p><StatusBadge status={x.status || "submitted"} /></div>
                </div>
              ); })}
            </div>
          </div>
        )}
        <RequestRawData r={r} leads={leads} />
        <AllFieldsPanel path="requests" id={r.id}
          extraDocs={[{ label: "Private address", path: `requests/${r.id}/private`, id: "address" }, { label: "Private contact", path: `requests/${r.id}/private`, id: "contact" }]}
          related={[
            { label: "Lead distributions (pros floated / selected)", path: "lead_distributions", filters: [["request_id", "==", r.id]], describe: (d) => `${d.professional_id} · ${d.status}${d.phase ? ` · ${d.phase}` : ""}` },
            { label: "Professional responses", path: "professional_responses", filters: [["request_id", "==", r.id]], describe: (d) => `${d.professional_id} · ${d.status}` },
          ]} />
      </SheetContent>
    </Sheet>
  );
}

function Section({ icon, title, children }) {
  const Icon = Icons[icon] || Icons.Circle;
  return (
    <div className="mt-5">
      <h4 className="font-heading font-semibold text-foreground mb-2 flex items-center gap-2 text-sm"><Icon className="h-4 w-4 text-primary" /> {title}</h4>
      <div className="rounded-lg border border-border p-3 space-y-1.5">{children}</div>
    </div>
  );
}
const KV = ({ k, v }) => <div className="flex justify-between gap-3 text-sm"><span className="text-muted-foreground capitalize shrink-0">{k}</span><span className="text-foreground text-right break-words">{v}</span></div>;
